import { getTokenFromUrlHash, getWindowUrl, isTokenFormat } from '@onefootprint/core';
import { hostedOnboardingD2pStatusOptions } from '@onefootprint/fetch-hosted';
import type { D2pSessionStatus } from '@onefootprint/request-types';
import { createFileRoute } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { NoWebauthnSupport, PasskeyCancelled, PasskeyError, PasskeySuccess, SessionExpired } from '@/idv2/features/handoff';
import RegisterPasskey from '@/idv2/features/liveness/register-passkey';
import useD2pStatusPoll from '@/idv2/hooks/use-d2p-status-poll';
import { checkDeviceInfo, type DeviceInfo } from '@/idv2/utils/get-device';
import { getLogger } from '@/idv2/utils/logger';
const {
  logWarn
} = getLogger({
  name: 'idv2',
  location: 'handoff'
});
type HandoffState = 'inProgress' | 'completed' | 'canceled' | 'expired' | 'error' | 'noWebauthnSupport';
const d2pStatusToState = (status: D2pSessionStatus): HandoffState => {
  switch (status) {
    case 'waiting':
    case 'in_progress':
      return 'inProgress';
    case 'completed':
      return 'completed';
    case 'canceled':
      return 'canceled';
    case 'failed':
      return 'expired';
    default:
      return 'error';
  }
};
export const Route = createFileRoute('/handoff')({
  beforeLoad: () => {
    const windowUrl = getWindowUrl();
    const authToken = getTokenFromUrlHash(windowUrl) || '';
    const valid = isTokenFormat(authToken);
    if (!valid) {
      logWarn('Invalid token format');
    }
    return {
      authToken,
      valid
    };
  },
  loader: async ({
    context
  }) => {
    const {
      authToken,
      valid,
      queryClient
    } = context;
    if (!valid) {
      return {
        authToken,
        device: undefined,
        initialState: 'error' as const
      };
    }
    let device: DeviceInfo | undefined;
    try {
      device = await checkDeviceInfo();
    } catch (error) {
      logWarn('Unable to collect device info', error);
      return {
        authToken,
        device: undefined,
        initialState: 'error' as const
      };
    }
    if (!device.hasSupportForWebauthn) {
      return {
        authToken,
        device,
        initialState: 'noWebauthnSupport' as const
      };
    }
    try {
      const d2pData = await queryClient.fetchQuery(hostedOnboardingD2pStatusOptions({
        headers: {
          'X-Fp-Authorization': authToken
        }
      }));
      return {
        authToken,
        device,
        initialState: d2pStatusToState(d2pData.status)
      };
    } catch (error) {
      logWarn('Failed to fetch initial D2P status', error);
      return {
        authToken,
        device,
        initialState: 'error' as const
      };
    }
  },
  component: HandoffRoute
});
function HandoffRoute() {
  const {
    authToken,
    device,
    initialState
  } = Route.useLoaderData();
  const [state, setState] = useState<HandoffState>(initialState);
  const isOptimisticCompleted = useRef(false);
  const pollEnabled = state !== 'error' && state !== 'noWebauthnSupport' && !isOptimisticCompleted.current;
  const d2pPoll = useD2pStatusPoll({
    authToken,
    enabled: pollEnabled,
    onStatusChange: status => {
      const newState = d2pStatusToState(status);
      setState(newState);

      // When we locally completed passkey registration, update server status
      if (isOptimisticCompleted.current && (status === 'waiting' || status === 'in_progress')) {
        d2pPoll.updateStatus('completed');
      }
    }
  });

  // Update D2P status to in_progress when we start passkey registration
  const handlePasskeyStart = () => {
    if (d2pPoll.status === 'waiting') {
      d2pPoll.updateStatus('in_progress');
    }
  };
  if (state === 'inProgress' && authToken && device && !isOptimisticCompleted.current) {
    handlePasskeyStart();
    return <RegisterPasskey deviceInfo={device} isInIframe={false} token={authToken} actionKind="add_primary" onComplete={() => {
      isOptimisticCompleted.current = true;
      setState('completed');
      d2pPoll.updateStatus('completed');
    }} />;
  }
  if (state === 'completed' || isOptimisticCompleted.current) return <PasskeySuccess />;
  if (state === 'canceled') return <PasskeyCancelled />;
  if (state === 'expired') return <SessionExpired />;
  if (state === 'noWebauthnSupport') return <NoWebauthnSupport />;
  return <PasskeyError />;
}